// src/utils/exportUtils.js - Enhanced PDF Export with Better Similar Cases
import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, BorderStyle, WidthType, AlignmentType } from 'docx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Helper to strip HTML tags from text
const stripHtml = (html) => {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || '';
};

// Convert markdown to plain text (simple version)
const markdownToPlainText = (markdown) => {
  if (!markdown) return '';
  
  // Replace headers
  let text = markdown.replace(/#{1,6}\s+(.*)/g, '$1\n');
  
  // Replace bold and italic
  text = text.replace(/\*\*(.*?)\*\*/g, '$1');
  text = text.replace(/__(.*?)__/g, '$1');
  text = text.replace(/\*(.*?)\*/g, '$1');
  text = text.replace(/_(.*?)_/g, '$1');
  
  // Replace links
  text = text.replace(/\[(.*?)\]\((.*?)\)/g, '$1 ($2)');
  
  // Replace lists
  text = text.replace(/^\s*[-*+]\s+(.*)/gm, '• $1');
  text = text.replace(/^\s*\d+\.\s+(.*)/gm, '• $1');
  
  // Replace code blocks
  text = text.replace(/```[a-z]*\n([\s\S]*?)```/g, '$1');
  text = text.replace(/`([^`]+)`/g, '$1');
  
  // Replace blockquotes
  text = text.replace(/^\s*>\s+(.*)/gm, '$1');
  
  return text;
};

// Function to format date for display
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleString();
  } catch (e) {
    return dateString;
  }
};

// Function to check if a value is a date and format it
const formatPossibleDate = (value) => {
  if (!value) return 'N/A';
  
  // Check if value looks like a date
  if (/^\d{4}-\d{2}-\d{2}/.test(value) || /^\d{1,2}\/\d{1,2}\/\d{4}/.test(value)) {
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
      }
    } catch (e) {
      // Not a valid date, return as is
    }
  }
  
  return value;
};

// Simplified Word export function without advanced features that might cause issues
export const exportToWord = async (queryData) => {
  try {
    if (!queryData) {
      throw new Error('No data to export');
    }

    // Create document with basic styling
    const doc = new Document({
      sections: [{
        properties: {},
        children: [
          // Title
          new Paragraph({
            text: 'eWakili Legal Research',
            heading: HeadingLevel.TITLE,
            spacing: { after: 400 }
          }),
          
          // Question
          new Paragraph({
            text: 'Research Question',
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 400, after: 200 }
          }),
          new Paragraph({
            text: queryData.question || 'N/A',
            spacing: { after: 400 }
          }),
          
          // Type if available
          ...(queryData.question_type ? [
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Query Type: ',
                  bold: true
                }),
                new TextRun({
                  text: queryData.question_type
                })
              ],
              spacing: { after: 400 }
            })
          ] : []),
          
          // Result Section
          ...(queryData.result ? [
            new Paragraph({
              text: 'Research Results',
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 }
            }),
            new Paragraph({
              text: markdownToPlainText(queryData.result),
              spacing: { after: 400 }
            })
          ] : []),
          
          // Metadata Table
          new Paragraph({
            text: 'Query Information',
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 }
          }),
          
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: [
              new TableRow({
                children: [
                  new TableCell({
                    children: [new Paragraph('Status')],
                    width: { size: 30, type: WidthType.PERCENTAGE }
                  }),
                  new TableCell({
                    children: [new Paragraph(queryData.status || 'N/A')]
                  })
                ]
              }),
              new TableRow({
                children: [
                  new TableCell({
                    children: [new Paragraph('Date Created')]
                  }),
                  new TableCell({
                    children: [new Paragraph(new Date(queryData.date_created).toLocaleString() || 'N/A')]
                  })
                ]
              }),
              new TableRow({
                children: [
                  new TableCell({
                    children: [new Paragraph('Credits Used')]
                  }),
                  new TableCell({
                    children: [new Paragraph(queryData.charges?.toString() || 'N/A')]
                  })
                ]
              }),
              new TableRow({
                children: [
                  new TableCell({
                    children: [new Paragraph('Processing Time')]
                  }),
                  new TableCell({
                    children: [new Paragraph(queryData.completion_time ? `${queryData.completion_time.toFixed(2)}s` : 'N/A')]
                  })
                ]
              })
            ]
          }),
          
          // Similar Cases Section (just title and count)
          ...(queryData.similar_cases && queryData.similar_cases.length > 0 ? [
            new Paragraph({
              text: 'Similar Cases',
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 }
            }),
            new Paragraph({
              text: `Number of similar cases: ${queryData.similar_cases.length}`,
              spacing: { after: 200 }
            }),
            ...queryData.similar_cases.map((caseItem, index) => 
              new Paragraph({
                text: `Case ${index + 1}: ${caseItem.case?.metadata_citation || 'Unnamed Case'}`,
                spacing: { after: 100 }
              })
            )
          ] : []),
          
          // Footer
          new Paragraph({
            text: 'Generated by eWakili Kenya Legal AI',
            spacing: { before: 800 }
          }),
          new Paragraph({
            text: new Date().toLocaleString()
          })
        ],
      }],
    });

    // Generate and download the document
    const buffer = await Packer.toBuffer(doc);
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
    saveAs(blob, `eWakili_Research_${queryData.id}.docx`);
    
    return true;
  } catch (error) {
    console.error('Error exporting to Word:', error);
    return false;
  }
};

// Enhanced PDF export with better similar cases section
export const exportToPdf = (queryData) => {
  try {
    if (!queryData) {
      throw new Error('No data to export');
    }

    // Create PDF document
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const margin = 14; // Left margin
    const contentWidth = pageWidth - (margin * 2); // Available width for content
    
    // Helper function to add section header
    const addSectionHeader = (text, y) => {
      doc.setFillColor(65, 105, 225); // Royal blue
      doc.rect(margin, y, contentWidth, 10, 'F');
      doc.setTextColor(255, 255, 255); // White text
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(text, margin + 4, y + 7);
      return y + 15; // Return new Y position with some spacing
    };
    
    // Add document title with styling
    doc.setFillColor(25, 25, 112); // Navy blue background
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setTextColor(255, 255, 255); // White text
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('eWakili Legal Research', pageWidth / 2, 15, { align: 'center' });
    
    // Additional decorative element - add a colored stripe below the header
    doc.setFillColor(65, 105, 225); // Royal blue
    doc.rect(0, 25, pageWidth, 3, 'F');
    
    // Set starting position after header
    let yPos = 40;
    
    // Add research question section
    yPos = addSectionHeader('RESEARCH QUESTION', yPos);
    
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0); // Black text
    doc.setFont('helvetica', 'normal');
    
    // Handle question text with improved wrapping
    const questionLines = doc.splitTextToSize(queryData.question || 'N/A', contentWidth);
    doc.text(questionLines, margin, yPos);
    
    // Move Y position based on text height
    yPos += questionLines.length * 7 + 5;
    
    // Add query type if available
    if (queryData.question_type) {
      doc.setFont('helvetica', 'bold');
      doc.text('Query Type:', margin, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(queryData.question_type, margin + 30, yPos);
      yPos += 10;
    }
    
    // Add decorative separator
    doc.setDrawColor(65, 105, 225); // Royal blue
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 10;
    
    // Add results if available with better formatting
    if (queryData.result) {
      yPos = addSectionHeader('RESEARCH RESULTS', yPos);
      
      doc.setTextColor(0, 0, 0); // Black text
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      
      // Format result text with improved wrapping and chunking to prevent cutoff
      const plainResult = markdownToPlainText(queryData.result);
      
      // Split into paragraphs to handle them separately
      const resultParagraphs = plainResult.split('\n\n');
      
      // Process each paragraph
      resultParagraphs.forEach((paragraph) => {
        if (paragraph.trim() === '') return;
        
        // Check if need to add a new page
        if (yPos > doc.internal.pageSize.height - 20) {
          doc.addPage();
          yPos = 20;
        }
        
        // Check if paragraph starts with a bullet point
        const isBulletPoint = paragraph.trim().startsWith('•');
        let leftMargin = margin;
        
        // Increase margin for bullet points
        if (isBulletPoint) {
          leftMargin = margin + 5;
        }
        
        // Check if paragraph might be a heading (ends with a colon)
        if (paragraph.trim().endsWith(':') && paragraph.length < 100) {
          doc.setFont('helvetica', 'bold');
        }
        
        // Split long paragraphs into lines
        const paragraphLines = doc.splitTextToSize(paragraph, contentWidth - (leftMargin - margin));
        
        // Handle multi-page paragraphs
        if (yPos + paragraphLines.length * 6 > doc.internal.pageSize.height - 20) {
          // Calculate how many lines fit on this page
          const linesOnFirstPage = Math.floor((doc.internal.pageSize.height - yPos - 20) / 6);
          
          // Print what fits on current page
          const firstPageLines = paragraphLines.slice(0, linesOnFirstPage);
          doc.text(firstPageLines, leftMargin, yPos);
          
          // Add new page
          doc.addPage();
          yPos = 20;
          
          // Print rest of paragraph
          const remainingLines = paragraphLines.slice(linesOnFirstPage);
          doc.text(remainingLines, leftMargin, yPos);
          yPos += remainingLines.length * 6 + 4;
        } else {
          doc.text(paragraphLines, leftMargin, yPos);
          yPos += paragraphLines.length * 6 + 4;
        }
        
        // Reset font
        doc.setFont('helvetica', 'normal');
      });
      
      // Add some extra spacing after result
      yPos += 5;
    }
    
    // Add decorative separator
    doc.setDrawColor(65, 105, 225); // Royal blue
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 10;
    
    // Check if we need a new page for metadata
    if (yPos > doc.internal.pageSize.height - 70) {
      doc.addPage();
      yPos = 20;
    }
    
    // Add query metadata table with improved styling
    yPos = addSectionHeader('QUERY INFORMATION', yPos);
    
    // Enhanced metadata table with better styling
    const tableData = [
      ['Status', queryData.status || 'N/A'],
      ['Date Created', new Date(queryData.date_created).toLocaleString()],
      ['Credits Used', queryData.charges?.toString() || 'N/A'],
      ['Processing Time', queryData.completion_time ? `${queryData.completion_time.toFixed(2)}s` : 'N/A']
    ];
    
    doc.autoTable({
      startY: yPos,
      head: [['Property', 'Value']],
      body: tableData,
      theme: 'grid',
      headStyles: { 
        fillColor: [65, 105, 225],
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [240, 240, 255]
      },
      margin: { left: margin, right: margin },
      styles: {
        fontSize: 11,
        cellPadding: 5
      }
    });
    
    // Get Y position after the table
    yPos = doc.previousAutoTable.finalY + 15;
    
    // Add similar cases section with enhanced information and visually indicated links
    if (queryData.similar_cases && queryData.similar_cases.length > 0) {
      // Check if we need a new page
      if (yPos + 60 > doc.internal.pageSize.height) {
        doc.addPage();
        yPos = 20;
      }
      
      yPos = addSectionHeader('SIMILAR CASES', yPos);
      
      // Add explanatory text about links
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100); // Gray text
      doc.setFont('helvetica', 'italic');
      doc.text('The following cases are related to your research query. Visit the web interface to open full case details.', 
               margin, yPos);
      yPos += 8;
      
      // Add visual link indicator
      doc.setTextColor(0, 0, 255); // Blue text
      doc.setFont('helvetica', 'bold');
      doc.text('Blue underlined text indicates clickable links in the web interface', margin, yPos);
      yPos += 10;
      
      // Prepare case data with improved information
      const headColumns = ['#', 'Case Citation', 'Court', 'Date', 'Type'];
      
      // Format case data for table with more information
      const casesData = queryData.similar_cases.map((caseItem, index) => {
        const citation = caseItem.case?.metadata_citation || 'Unnamed Case';
        const court = caseItem.case?.metadata_court || 'N/A';
        const date = formatPossibleDate(caseItem.case?.metadata_date_delivered || caseItem.case?.date_filed);
        const type = caseItem.case?.metadata_case_type || caseItem.case?.case_type || 'N/A';
        
        return [
          `Case ${index + 1}`,
          citation,
          court,
          date,
          type
        ];
      });
      
      // Function to add case info notes after table
      const addCaseNotes = (startY) => {
        let y = startY + 5;
        
        doc.setFontSize(11);
        doc.setTextColor(50, 50, 50);
        doc.setFont('helvetica', 'bold');
        doc.text('Case Details & Highlights:', margin, y);
        y += 7;
        
        // Add notes about each case with important details
        queryData.similar_cases.forEach((caseItem, index) => {
          if (y > doc.internal.pageSize.height - 30) {
            doc.addPage();
            y = 20;
          }
          
          // Case number and citation
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(25, 25, 112); // Navy blue
          
          const caseTitle = `Case ${index + 1}: ${caseItem.case?.metadata_citation || 'Unnamed Case'}`;
          const caseTitleLines = doc.splitTextToSize(caseTitle, contentWidth);
          doc.text(caseTitleLines, margin, y);
          y += caseTitleLines.length * 6;
          
          // If we have case summary, show it
          if (caseItem.case_summary) {
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(0, 0, 0);
            
            const summaryLines = doc.splitTextToSize(
              `Summary: ${caseItem.case_summary.substring(0, 2000)}${caseItem.case_summary.length > 2000 ? '...' : ''}`, 
              contentWidth - 5
            );
            
            doc.text(summaryLines, margin + 5, y);
            y += summaryLines.length * 5 + 3;
          }
          
          // Show key info like judges, parties, etc.
          const keyInfoItems = [];
          
          if (caseItem.case?.metadata_judges) {
            keyInfoItems.push(`Judges: ${caseItem.case.metadata_judges}`);
          }
          
          if (caseItem.case?.metadata_parties) {
            keyInfoItems.push(`Parties: ${caseItem.case.metadata_parties}`);
          }
          
          if (caseItem.case?.metadata_advocate) {
            keyInfoItems.push(`Advocates: ${caseItem.case.metadata_advocate}`);
          }
          
          // Add legal principles if available (limited to first 2)
          if (caseItem.legal_principles && caseItem.legal_principles.length > 0) {
            const principles = caseItem.legal_principles.slice(0, 2).map(p => 
              p.principle || p.principle_text || 'Legal principle').join('; ');
            keyInfoItems.push(`Key Principles: ${principles}`);
          }
          
          // Add similar percentage if available
          if (caseItem.similarity !== undefined) {
            const similarityPercent = Math.round(caseItem.similarity * 100);
            keyInfoItems.push(`Similarity: ${similarityPercent}% match to your query`);
          }
          
          // Display key info items
          doc.setFontSize(9);
          doc.setTextColor(80, 80, 80);
          
          keyInfoItems.forEach(item => {
            if (y > doc.internal.pageSize.height - 15) {
              doc.addPage();
              y = 20;
            }
            
            const itemLines = doc.splitTextToSize(item, contentWidth - 10);
            doc.text(itemLines, margin + 5, y);
            y += itemLines.length * 4 + 2;
          });
          
          // Add link text
          doc.setTextColor(0, 0, 255);
          doc.setFont('helvetica', 'italic');
          doc.text('Click on this case in the web interface to view complete details', margin + 5, y);
          
          // Add separator line
          y += 7;
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(0.2);
          doc.line(margin, y, pageWidth - margin, y);
          y += 10;
        });
        
        return y; // Return new Y position
      };
      
      // Create cases table with better styling and clickable links visual indicator
      doc.autoTable({
        startY: yPos,
        head: [headColumns],
        body: casesData,
        theme: 'grid',
        headStyles: {
          fillColor: [65, 105, 225],
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 'auto', textColor: [0, 0, 255], fontStyle: 'italic' }, // Make citations look like links
          2: { cellWidth: 60 },
          3: { cellWidth: 30 },
          4: { cellWidth: 30 }
        },
        alternateRowStyles: {
          fillColor: [240, 240, 255]
        },
        margin: { left: margin, right: margin },
        styles: {
          fontSize: 10,
          cellPadding: 4
        },
        willDrawCell: function(data) {
          // Add visual indicator for clickable links (column 1 = citation)
          if (data.column.index === 1 && data.section === 'body') {
            // Draw underline for "link" effect
            const x = data.cell.x;
            const y = data.cell.y + data.cell.height - 1;
            const w = data.cell.width;
            
            doc.setDrawColor(0, 0, 255); // Blue underline
            doc.setLineWidth(0.2);
            doc.line(x, y, x + w, y);
          }
        }
      });
      
      // Add detailed case information below the table
      yPos = addCaseNotes(doc.previousAutoTable.finalY);
    }
    
    // Add footer with improved styling
    const addFooter = () => {
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        
        // Add footer background
        doc.setFillColor(245, 245, 255);
        doc.rect(0, doc.internal.pageSize.height - 20, pageWidth, 20, 'F');
        
        // Add footer text
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        doc.setFont('helvetica', 'normal');
        doc.text(
          `Generated by eWakili Kenya Legal AI | ${new Date().toLocaleString()} | Page ${i} of ${pageCount}`,
          pageWidth / 2,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
        
        // Add thin line above footer
        doc.setDrawColor(65, 105, 225);
        doc.setLineWidth(0.5);
        doc.line(0, doc.internal.pageSize.height - 20, pageWidth, doc.internal.pageSize.height - 20);
      }
    };
    
    // Add footer to all pages
    addFooter();
    
    // Save the PDF
    doc.save(`eWakili_Research_${queryData.id}.pdf`);
    
    return true;
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    return false;
  }
};